import React from 'react';
import ReactDOM from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuth from '../hooks/useAuth';
import api from '../services/api';
import { useLayout } from '../contexts/LayoutContext';
import { temGestaoLicencas } from '../utils/roles';
import {
  acordeaoMenu,
  contextoDoUsuario,
  grupoDaRota,
  itensDoGrupo,
  limparMemoriaGruposAntiga,
  montarMenuLateral,
} from '../config/menuLateral';
import '../styles/Sidebar.css';
import { useAcessoSoftwares } from '../config/softwares';

// A organização do menu (itens soltos, grupos e seções por papel) e as condições
// de visibilidade de cada item ficam em config/menuLateral.js. Aqui só se desenha.

const item = (icon, label) => (
  <>
    <span className="sidebar-icon" aria-hidden="true">{icon}</span>
    <span className="sidebar-label">{label}</span>
  </>
);

// Rotulo de subitem: quando nao cabe, rola com o mouse em cima (Sidebar.css,
// .sidebar-rotulo) para mostrar o texto inteiro.
const RotuloRolante = ({ children }) => (
  <span className="sidebar-rotulo"><span>{children}</span></span>
);

const InlineSubmenu = ({ id, children }) => (
  <div className="sidebar-inline-submenu" id={id}>
    <div className="sidebar-inline-submenu-inner">{children}</div>
  </div>
);

// left do botão toggle alinhado à borda direita do sidebar (expanded=230, collapsed=64)
const TOGGLE_LEFT_EXPANDED = 222;
const TOGGLE_LEFT_COLLAPSED = 58;

const ID_FLYOUT = 'sidebar-flyout';
const linksDoFlyout = (el) => (el ? Array.from(el.querySelectorAll('a')) : []);

const Sidebar = ({ collapsed: collapsedProp, onToggle }) => {
  const { usuario } = useAuth();
  const location = useLocation();
  const { mobileSidebarOpen, closeMobileSidebar } = useLayout();
  const { t } = useTranslation('sidebar');

  // No mobile (≤768px) a sidebar é sempre um drawer expandido: ignoramos o estado
  // "collapsed" (que é do desktop) para que os submenus apareçam em acordeão inline.
  const [isMobile, setIsMobile] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const collapsed = collapsedProp && !isMobile;
  const papel = usuario?.tipo_usuario;
  const [cloudEnabled, setCloudEnabled] = React.useState(false);
  const gereLicencas = temGestaoLicencas(usuario);
  // Chaves com suspeita de clone: selo no item Licenças, para o alerta ficar
  // visível em qualquer tela. A própria página avisa quando o número muda.
  const [alertasLicencas, setAlertasLicencas] = React.useState(0);
  // Softwares: a API diz se o papel ativo vê a página (config/softwares.js).
  const acessoSoftwares = useAcessoSoftwares(usuario);

  const menu = React.useMemo(
    () => (usuario
      ? montarMenuLateral(contextoDoUsuario(usuario, {
        cloudHabilitado: cloudEnabled,
        softwaresVisivel: acessoSoftwares.visivel,
      }))
      : []),
    [usuario, cloudEnabled, acessoSoftwares.visivel]
  );
  const grupoAtual = grupoDaRota(menu, location.pathname);

  // ── Grupo aberto (menu expandido, em acordeão) ──────────────────────────────
  // Um grupo por vez: o da página atual, ou o último cabeçalho clicado
  // (config/menuLateral.js, acordeaoMenu). Nada fica guardado no navegador.
  const [grupoAberto, despacharGrupo] = React.useReducer(acordeaoMenu, grupoAtual);

  // A antiga memória de grupos abertos (localStorage) foi aposentada: limpa.
  React.useEffect(() => { limparMemoriaGruposAntiga(); }, []);

  // A cada navegação (location.key muda até ao clicar de novo no item atual) só
  // fica aberto o grupo da página; item solto da raiz fecha todos.
  React.useEffect(() => {
    despacharGrupo({ tipo: 'navegar', arvore: menu, caminho: location.pathname });
    // `menu` fica de fora de propósito: recalcular o menu (ex.: o acesso ao Cloud
    // chegou) não pode reabrir um grupo que o usuário fechou à mão.
  }, [location.key, location.pathname, grupoAtual, papel]);

  // ── Tooltip simples (itens sem filhos) ──────────────────────────────────────
  const [tooltip, setTooltip] = React.useState(null); // { label, y }

  // ── Flyout submenu (grupos, menu recolhido) ─────────────────────────────────
  const [flyout, setFlyout] = React.useState(null); // { id, label, filhos, y }
  const hideTimer = React.useRef(null);
  const flyoutRef = React.useRef(null);
  const cabecalhos = React.useRef({});
  const focarPrimeiro = React.useRef(false);
  const ignorarFoco = React.useRef(false);

  const scheduleHide = React.useCallback(() => {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      setFlyout(null);
      setTooltip(null);
    }, 150);
  }, []);

  const cancelHide = React.useCallback(() => {
    clearTimeout(hideTimer.current);
  }, []);

  React.useEffect(() => () => clearTimeout(hideTimer.current), []);

  // Fecha flyout/tooltip ao navegar
  React.useEffect(() => {
    setFlyout(null);
    setTooltip(null);
  }, [location.pathname]);

  // No mobile, fecha o drawer ao escolher um item. Os grupos são botões (não
  // links): só abrem o submenu e mantêm o drawer aberto.
  const handleSidebarNavClick = React.useCallback((e) => {
    const link = e.target.closest('a');
    if (!link) return;
    if (link.classList.contains('has-children')) return;
    closeMobileSidebar();
  }, [closeMobileSidebar]);

  // Fecha ao expandir
  React.useEffect(() => {
    if (!collapsed) {
      setFlyout(null);
      setTooltip(null);
    }
  }, [collapsed]);

  // Flyout que passaria do pé da tela sobe o necessário; e, se foi aberto pelo
  // teclado, o foco vai para o primeiro subitem.
  React.useLayoutEffect(() => {
    const el = flyoutRef.current;
    if (!flyout || !el) return;
    const margem = 8;
    const altura = el.getBoundingClientRect().height;
    const limite = window.innerHeight - margem;
    if (flyout.y + altura > limite) {
      el.style.top = `${Math.max(margem, limite - altura)}px`;
    }
    if (focarPrimeiro.current) {
      focarPrimeiro.current = false;
      const [primeiro] = linksDoFlyout(el);
      if (primeiro) primeiro.focus();
    }
  }, [flyout]);

  // Props para item simples (sem filhos)
  const tip = (label) => {
    if (!collapsed) return {};
    const mostrar = (e) => {
      cancelHide();
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltip({ label, y: rect.top + rect.height / 2 });
      setFlyout(null);
    };
    // Recolhida, o rótulo some (display: none): o nome acessível vem daqui.
    return {
      'aria-label': label,
      onMouseEnter: mostrar,
      onFocus: mostrar,
      onMouseLeave: scheduleHide,
      onBlur: scheduleHide,
    };
  };

  const abrirFlyout = (no, alvo, { focar = false } = {}) => {
    cancelHide();
    const rect = alvo.getBoundingClientRect();
    focarPrimeiro.current = focar;
    setFlyout({ id: no.id, label: t(no.rotulo), filhos: no.filhos, y: rect.top });
    setTooltip(null);
  };

  const fecharFlyoutVoltando = (id) => {
    setFlyout(null);
    const cabecalho = cabecalhos.current[id];
    if (cabecalho) {
      ignorarFoco.current = true;
      cabecalho.focus();
    }
  };

  // Sai o foco do cabeçalho/flyout: só fecha se não foi para dentro do par.
  const aoPerderFoco = (id) => (e) => {
    const destino = e.relatedTarget;
    if (destino && (flyoutRef.current?.contains(destino) || cabecalhos.current[id] === destino)) return;
    scheduleHide();
  };

  // Props do cabeçalho de grupo quando o menu está recolhido (só ícones).
  const propsGrupoRecolhido = (no) => ({
    'aria-haspopup': 'true',
    'aria-expanded': flyout?.id === no.id,
    'aria-controls': flyout?.id === no.id ? ID_FLYOUT : undefined,
    'aria-label': t(no.rotulo),
    onMouseEnter: (e) => abrirFlyout(no, e.currentTarget),
    onMouseLeave: scheduleHide,
    onFocus: (e) => {
      if (ignorarFoco.current) {
        ignorarFoco.current = false;
        return;
      }
      abrirFlyout(no, e.currentTarget);
    },
    onBlur: aoPerderFoco(no.id),
    // clique do mouse: o hover normalmente já abriu; só abre se não estiver aberto
    onClick: (e) => {
      if (flyout?.id !== no.id) abrirFlyout(no, e.currentTarget);
    },
    onKeyDown: (e) => {
      if (['Enter', ' ', 'ArrowRight', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        abrirFlyout(no, e.currentTarget, { focar: true });
        if (flyout?.id === no.id) {
          const [primeiro] = linksDoFlyout(flyoutRef.current);
          if (primeiro) primeiro.focus();
        }
      } else if (e.key === 'Escape' && flyout) {
        e.preventDefault();
        setFlyout(null);
      }
    },
  });

  // Teclado dentro do flyout: setas percorrem, Esc fecha e volta ao grupo,
  // Tab devolve o foco ao grupo e segue a ordem normal da página.
  const aoTeclarNoFlyout = (e) => {
    if (!flyout) return;
    const links = linksDoFlyout(flyoutRef.current);
    const atual = links.indexOf(document.activeElement);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const passo = e.key === 'ArrowDown' ? 1 : -1;
      const proximo = links[(atual + passo + links.length) % links.length];
      if (proximo) proximo.focus();
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      const alvo = e.key === 'Home' ? links[0] : links[links.length - 1];
      if (alvo) alvo.focus();
    } else if (e.key === 'Escape' || e.key === 'ArrowLeft') {
      e.preventDefault();
      fecharFlyoutVoltando(flyout.id);
    } else if (e.key === 'Tab') {
      // sem preventDefault: com o foco de volta no grupo, o Tab segue dali
      fecharFlyoutVoltando(flyout.id);
    }
  };

  React.useEffect(() => {
    const fetchDados = async () => {
      if (!usuario) return;
      try {
        if (!['admin', 'exxer', 'temporario'].includes(usuario.tipo_usuario)) {
          await api.get('/minhas-turmas');
          await api.get('/usuarios/contatos/me');
        }
      } catch (err) {
        console.error('Erro ao carregar dados da sidebar:', err);
      }
    };
    fetchDados();
  }, [usuario]);

  React.useEffect(() => {
    let alive = true;
    const fetchCloudAccess = async () => {
      if (!['admin', 'exxer', 'coordenador', 'professor', 'aluno'].includes(usuario?.tipo_usuario)) {
        setCloudEnabled(false);
        return;
      }
      try {
        const { data } = await api.get('/cloud/access');
        if (alive) setCloudEnabled(Boolean(data?.enabled));
      } catch (_err) {
        if (alive) setCloudEnabled(false);
      }
    };
    fetchCloudAccess();
    return () => { alive = false; };
  }, [usuario]);

  React.useEffect(() => {
    if (!gereLicencas) {
      setAlertasLicencas(0);
      return undefined;
    }
    let alive = true;
    api.get('/licencas/acesso')
      .then(({ data }) => { if (alive) setAlertasLicencas(Number(data?.alertas) || 0); })
      .catch(() => {});
    const aoMudar = (e) => setAlertasLicencas(Number(e.detail) || 0);
    window.addEventListener('licencas:alertas', aoMudar);
    return () => {
      alive = false;
      window.removeEventListener('licencas:alertas', aoMudar);
    };
  }, [gereLicencas]);

  if (!usuario) return null;

  const contagemBadge = (no) => (no.badge === 'licencas' ? alertasLicencas : 0);
  const selo = (quantidade) => quantidade > 0 && (
    <span className="sidebar-badge" title={t('licenseAlerts', { count: quantidade })}>{quantidade}</span>
  );

  const renderItemRaiz = (no) => (
    <NavLink key={no.id} to={no.rota} {...tip(t(no.rotulo))}>
      {item(no.icone, t(no.rotulo))}
      {selo(contagemBadge(no))}
    </NavLink>
  );

  const renderSubitem = (no) => (
    <NavLink key={no.id} className="sidebar-subitem" to={no.rota}>
      <span aria-hidden="true">{no.icone}</span> <RotuloRolante>{t(no.rotulo)}</RotuloRolante>
      {selo(contagemBadge(no))}
    </NavLink>
  );

  const renderGrupo = (no) => {
    const ativo = grupoAtual === no.id;
    const aberto = !collapsed && grupoAberto === no.id;
    const idSubmenu = `sidebar-grupo-${no.id}`;
    // grupo fechado (ou menu recolhido) mostra no cabeçalho o selo dos subitens
    const seloGrupo = aberto ? 0 : itensDoGrupo(no).reduce((total, f) => total + contagemBadge(f), 0);
    const propsCabecalho = collapsed
      ? propsGrupoRecolhido(no)
      : {
        'aria-expanded': aberto,
        'aria-controls': idSubmenu,
        onClick: () => despacharGrupo({ tipo: 'alternar', id: no.id }),
      };
    return (
      <React.Fragment key={no.id}>
        <button
          type="button"
          ref={(el) => { cabecalhos.current[no.id] = el; }}
          className={`sidebar-grupo has-children${ativo ? ' active' : ''}${aberto ? ' aberto' : ''}`}
          {...propsCabecalho}
        >
          {item(no.icone, t(no.rotulo))}
          {selo(seloGrupo)}
          <svg className="sidebar-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="9 6 15 12 9 18" />
          </svg>
        </button>
        {aberto && (
          <InlineSubmenu id={idSubmenu}>
            {no.filhos.map((filho) => (filho.tipo === 'secao' ? (
              <React.Fragment key={filho.id}>
                <div className="sidebar-secao">{t(filho.rotulo)}</div>
                {filho.filhos.map(renderSubitem)}
              </React.Fragment>
            ) : renderSubitem(filho)))}
          </InlineSubmenu>
        )}
      </React.Fragment>
    );
  };

  return (
    <>
      <div
        id="app-sidebar"
        role="navigation"
        aria-label={t('a11y.navigation')}
        className={`sidebar${collapsed ? ' collapsed' : ''}${mobileSidebarOpen ? ' mobile-open' : ''}`}
      >
        <div className="sidebar-links" onClick={handleSidebarNavClick}>
          {menu.map((no) => (no.tipo === 'grupo' ? renderGrupo(no) : renderItemRaiz(no)))}
        </div>
      </div>

      {/* Botão toggle com position:fixed — nunca cortado pelo overflow do sidebar */}
      <button
        type="button"
        className="sidebar-toggle"
        style={{ left: collapsed ? TOGGLE_LEFT_COLLAPSED : TOGGLE_LEFT_EXPANDED }}
        onClick={onToggle}
        title={collapsed ? t('expandMenu') : t('collapseMenu')}
        aria-label={collapsed ? t('expandMenu') : t('collapseMenu')}
        aria-expanded={!collapsed}
        aria-controls="app-sidebar"
      >
        {collapsed ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        )}
      </button>

      {/* Tooltip simples via portal */}
      {tooltip && collapsed && ReactDOM.createPortal(
        <div className="sidebar-tooltip" style={{ top: tooltip.y }} aria-hidden="true">
          {tooltip.label}
        </div>,
        document.body
      )}

      {/* Flyout submenu via portal */}
      {flyout && collapsed && ReactDOM.createPortal(
        <div
          id={ID_FLYOUT}
          ref={flyoutRef}
          className="sidebar-flyout"
          role="group"
          aria-label={flyout.label}
          style={{ top: flyout.y }}
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
          onFocus={cancelHide}
          onBlur={aoPerderFoco(flyout.id)}
          onKeyDown={aoTeclarNoFlyout}
        >
          <div className="sidebar-flyout-header" aria-hidden="true">{flyout.label}</div>
          {flyout.filhos.map((filho) => (filho.tipo === 'secao' ? (
            <React.Fragment key={filho.id}>
              <div className="sidebar-flyout-secao">{t(filho.rotulo)}</div>
              {filho.filhos.map((it) => (
                <NavLink key={it.id} to={it.rota} className="sidebar-flyout-item" onClick={() => setFlyout(null)}>
                  <span className="sidebar-flyout-icon" aria-hidden="true">{it.icone}</span>
                  {t(it.rotulo)}
                  {selo(contagemBadge(it))}
                </NavLink>
              ))}
            </React.Fragment>
          ) : (
            <NavLink key={filho.id} to={filho.rota} className="sidebar-flyout-item" onClick={() => setFlyout(null)}>
              <span className="sidebar-flyout-icon" aria-hidden="true">{filho.icone}</span>
              {t(filho.rotulo)}
              {selo(contagemBadge(filho))}
            </NavLink>
          )))}
        </div>,
        document.body
      )}
    </>
  );
};

export default Sidebar;
